/**
 * Questionnaire engine — renders any schema of the shape documented in
 * docs/qualifier-schema.md. One question per screen, answers persisted in
 * sessionStorage (never localStorage), routing and scoring delegated to
 * qf-evaluate.js.
 *
 * Mount:
 *   <div data-qf data-evaluate="…/qf-evaluate.js">
 *     <script type="application/json" data-qf-schema>…</script>
 *     <script type="application/json" data-qf-config>…</script>
 *   </div>
 */

const root = document.querySelector('[data-qf]');
if (root) boot(root);

async function boot(root) {
  const schema = JSON.parse(root.querySelector('[data-qf-schema]').textContent);
  const cfg = JSON.parse(root.querySelector('[data-qf-config]').textContent);
  const { evaluate, hardStop, isVisible } = await import(root.dataset.evaluate);

  const stage = root.querySelector('[data-qf-stage]');
  const countEl = root.querySelector('[data-qf-count]');
  const sectionEl = root.querySelector('[data-qf-section]');
  const barEl = root.querySelector('[data-qf-bar]');
  const progressEl = root.querySelector('[data-qf-progress]');
  const KEY = schema.storage_key;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------- tokens
  const tokens = cfg.tokens || {};
  const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '' && !/TODO/i.test(String(v));
  /** {name} or {name|template with %|fallback} */
  function fill(text) {
    return String(text || '').replace(/\{([a-z_]+)(?:\|([^|}]*)\|([^}]*))?\}/g, (m, name, tmpl, fallback) => {
      const v = tokens[name];
      if (tmpl === undefined) return hasValue(v) ? String(v) : '';
      return hasValue(v) ? tmpl.replace('%', String(v)) : fallback;
    });
  }

  // ----------------------------------------------------------------- state
  let state = load() || fresh();
  function fresh() {
    return { answers: {}, current: schema.screens[0].id, history: [], units: 'metric', startedAt: Date.now(), done: null };
  }
  function load() {
    try {
      const s = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      return s && s.answers && s.current ? s : null;
    } catch (e) { return null; }
  }
  function save() {
    try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode: keep going in memory */ }
  }
  function clear() {
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }

  const byId = Object.fromEntries(schema.screens.map((s) => [s.id, s]));
  const flow = () => schema.screens.filter((s) => isVisible(s, state.answers));
  const items = () => flow().filter((s) => s.type !== 'interstitial');
  const sectionTitle = (id) => (schema.sections.find((x) => x.id === id) || {}).title || '';

  const track = (name, data) => { try { window.ff2Track && window.ff2Track(name, { form: schema.id, ...data }); } catch (e) {} };

  // ------------------------------------------------------------ navigation
  function go(id, { push = true } = {}) {
    if (push && state.current && state.current !== id) state.history.push(state.current);
    state.current = id;
    save();
    render({ focus: true });
  }

  function next() {
    const f = flow();
    const i = f.findIndex((s) => s.id === state.current);
    const cur = f[i];
    if (cur) track('step_complete', { step_id: cur.id, step_index: stepIndex(cur) });

    const stop = hardStop(state.answers, schema);
    if (stop) return finishHardStop(stop);

    const nxt = f[i + 1];
    if (nxt) go(nxt.id);
  }

  function back() {
    const f = flow();
    let prev = state.history.pop();
    // Skip anything branching has since hidden.
    while (prev && !f.some((s) => s.id === prev)) prev = state.history.pop();
    if (prev) go(prev, { push: false });
  }

  function stepIndex(screen) {
    const list = items();
    const i = list.findIndex((s) => s.id === screen.id);
    return i >= 0 ? i + 1 : null;
  }

  // ------------------------------------------------------------- rendering
  const h = (tag, attrs = {}, ...kids) => {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === false || v === null || v === undefined) continue;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (const kid of kids.flat()) if (kid !== null && kid !== undefined && kid !== false) el.append(kid);
    return el;
  };

  let lastViewed = null;

  function updateProgress(screen) {
    const list = items();
    const total = list.length;
    let n;
    if (screen.type === 'interstitial') {
      const f = flow();
      const before = f.slice(0, f.findIndex((s) => s.id === screen.id)).filter((s) => s.type !== 'interstitial');
      n = before.length;
    } else {
      n = stepIndex(screen);
    }
    const answered = list.filter((s) => answeredScreen(s)).length;
    countEl.textContent = screen.type === 'interstitial' ? `${n} of ${total} answered` : `Question ${n} of ${total}`;
    sectionEl.textContent = screen.section ? sectionTitle(screen.section) : '';
    barEl.style.width = `${Math.round((answered / total) * 100)}%`;
    progressEl.setAttribute('aria-valuenow', String(answered));
    progressEl.setAttribute('aria-valuemax', String(total));
  }

  function answeredScreen(s) {
    const a = state.answers;
    switch (s.type) {
      case 'measure': return a.height_cm != null && a.weight_kg != null;
      case 'contact': return false;
      case 'multi': return Array.isArray(a[s.id]) && a[s.id].length > 0;
      case 'text': return s.optional ? a[s.id] !== undefined : String(a[s.id] || '').trim().length >= (s.min || 1);
      default: return a[s.id] !== undefined && a[s.id] !== '';
    }
  }

  function render({ focus = false } = {}) {
    if (state.done) return renderOutcome(state.done, { focus });
    const screen = byId[state.current] && isVisible(byId[state.current], state.answers) ? byId[state.current] : flow()[0];
    state.current = screen.id;
    root.dataset.state = 'question';
    progressEl.hidden = false;
    updateProgress(screen);

    const form = h('form', { class: 'qf-screen', novalidate: true, 'aria-labelledby': 'qf-label', 'data-screen': screen.id });
    const label = h('h1', { class: 'qf-q', id: 'qf-label', tabindex: '-1', text: fill(screen.label || '') });
    const help = screen.help ? h('p', { class: 'qf-help', id: 'qf-help', text: fill(screen.help) }) : null;
    const err = h('p', { class: 'qf-err', id: 'qf-err', role: 'alert', hidden: true });
    const showError = (msg, field) => {
      err.textContent = msg; err.hidden = false;
      if (field) { field.setAttribute('aria-invalid', 'true'); field.focus(); }
    };

    let controls; let onSubmit; let continueLabel = 'Continue'; let showContinue = true;

    switch (screen.type) {
      case 'interstitial': {
        const variant = (screen.variants || []).find((v) => matchesWhen(v.when));
        label.textContent = fill(variant ? variant.text : screen.text);
        label.classList.add('qf-q--note');
        onSubmit = () => next();
        break;
      }
      case 'single': {
        const opts = screen.options.filter((o) => !o.show_if_token || hasValue(tokens[o.show_if_token]));
        const cur = state.answers[screen.id];
        controls = h('div', { class: 'qf-opts', role: 'group', 'aria-labelledby': 'qf-label', 'aria-describedby': help ? 'qf-help' : null });
        opts.forEach((o, i) => {
          const b = h('button', {
            type: 'button', class: 'qf-opt', 'aria-pressed': String(cur === o.value), 'data-value': o.value,
            onclick: () => choose(o.value, b),
          }, h('span', { class: 'qf-key', 'aria-hidden': 'true', text: i < 9 ? String(i + 1) : '' }), h('span', { text: fill(o.label) }));
          controls.append(b);
        });
        showContinue = cur !== undefined;
        onSubmit = () => {
          if (state.answers[screen.id] === undefined) return showError('Pick one of the options to continue.');
          next();
        };
        function choose(value, btn) {
          state.answers[screen.id] = value; save();
          controls.querySelectorAll('.qf-opt').forEach((x) => x.setAttribute('aria-pressed', String(x === btn)));
          setTimeout(next, reduceMotion ? 0 : 140);
        }
        break;
      }
      case 'multi': {
        const cur = new Set(state.answers[screen.id] || []);
        controls = h('div', { class: 'qf-opts', role: 'group', 'aria-labelledby': 'qf-label', 'aria-describedby': help ? 'qf-help' : null });
        screen.options.forEach((o, i) => {
          const b = h('button', {
            type: 'button', class: 'qf-opt qf-opt--multi', role: 'checkbox', 'aria-checked': String(cur.has(o.value)), 'data-value': o.value,
            onclick: () => toggle(o.value),
          }, h('span', { class: 'qf-key', 'aria-hidden': 'true', text: i < 9 ? String(i + 1) : '' }), h('span', { text: fill(o.label) }));
          controls.append(b);
        });
        function toggle(value) {
          const ex = screen.exclusive || [];
          if (cur.has(value)) cur.delete(value);
          else {
            if (ex.includes(value)) cur.clear();
            else ex.forEach((x) => cur.delete(x));
            cur.add(value);
          }
          state.answers[screen.id] = [...cur]; save();
          controls.querySelectorAll('.qf-opt').forEach((x) => x.setAttribute('aria-checked', String(cur.has(x.dataset.value))));
          err.hidden = true;
        }
        onSubmit = () => {
          if (!cur.size) return showError('Pick at least one, or "Nothing structured yet".');
          next();
        };
        break;
      }
      case 'text': {
        const id = 'qf-in';
        const attrs = { id, class: 'qf-input', name: screen.id, maxlength: String(screen.max || 2000), 'aria-labelledby': 'qf-label', 'aria-describedby': [help ? 'qf-help' : '', 'qf-count', 'qf-err'].filter(Boolean).join(' ') };
        const field = screen.multiline ? h('textarea', { ...attrs, rows: '5' }) : h('input', { ...attrs, type: 'text', autocomplete: 'off' });
        field.value = state.answers[screen.id] || '';
        const counter = h('p', { class: 'qf-count small', id: 'qf-count' });
        const upd = () => {
          const len = field.value.trim().length;
          counter.textContent = screen.optional ? (len ? `${len} characters` : 'Optional') : (len >= screen.min ? `${len} characters` : `${len} of at least ${screen.min} characters`);
        };
        field.addEventListener('input', () => {
          state.answers[screen.id] = field.value; save(); upd();
          field.removeAttribute('aria-invalid'); err.hidden = true;
        });
        if (screen.multiline) {
          field.addEventListener('keydown', (e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); form.requestSubmit(); } });
        }
        upd();
        controls = h('div', { class: 'qf-field' }, field, counter);
        onSubmit = () => {
          const len = field.value.trim().length;
          if (!screen.optional && len < (screen.min || 1)) {
            return showError(`Write at least ${screen.min} characters, a sentence or two is enough. You're at ${len}.`, field);
          }
          state.answers[screen.id] = field.value.trim(); save();
          next();
        };
        break;
      }
      case 'number': {
        const field = h('input', {
          id: 'qf-in', class: 'qf-input qf-input--num qf-input--short', type: 'text', inputmode: 'numeric', pattern: '[0-9]*',
          autocomplete: 'off', placeholder: screen.placeholder || '', 'aria-labelledby': 'qf-label',
          'aria-describedby': [help ? 'qf-help' : '', 'qf-err'].filter(Boolean).join(' '),
        });
        const cur = state.answers[screen.id];
        field.value = cur != null ? String(cur) : '';
        field.addEventListener('input', () => { field.removeAttribute('aria-invalid'); err.hidden = true; });
        controls = h('div', { class: 'qf-mrow' }, field, screen.unit ? h('span', { class: 'qf-unit-suffix', text: screen.unit }) : null);
        onSubmit = () => {
          const raw = field.value.trim();
          const n = Number(raw);
          if (!/^\d{1,3}$/.test(raw) || n < screen.min || n > screen.max) {
            return showError(`Type your age as a number between ${screen.min} and ${screen.max}, for example 29.`, field);
          }
          state.answers[screen.id] = n; save();
          next();
        };
        break;
      }
      case 'slider': {
        const cur = state.answers[screen.id];
        const out = h('output', { class: 'qf-slider__val num', for: 'qf-range', text: cur != null ? String(cur) : '–' });
        const range = h('input', {
          type: 'range', id: 'qf-range', class: 'qf-range', min: String(screen.min), max: String(screen.max), step: '1',
          value: String(cur != null ? cur : Math.round((screen.min + screen.max) / 2)),
          'aria-labelledby': 'qf-label', 'aria-valuetext': cur != null ? `${cur} out of ${screen.max}` : 'Not chosen yet',
        });
        const set = () => {
          state.answers[screen.id] = Number(range.value); save();
          out.textContent = range.value;
          range.setAttribute('aria-valuetext', `${range.value} out of ${screen.max}`);
          range.classList.add('is-set'); err.hidden = true;
        };
        range.addEventListener('input', set);
        range.addEventListener('change', set);
        if (cur != null) range.classList.add('is-set');
        controls = h('div', { class: 'qf-slider' }, out, range,
          h('div', { class: 'qf-slider__ends small' }, h('span', { text: `${screen.min}: ${screen.min_label || ''}` }), h('span', { text: `${screen.max}: ${screen.max_label || ''}` })));
        onSubmit = () => {
          if (state.answers[screen.id] == null) return showError('Move the slider to choose a number from 1 to 10.', range);
          next();
        };
        break;
      }
      case 'measure': {
        controls = renderMeasure(showError, err);
        onSubmit = controls._submit;
        break;
      }
      case 'contact': {
        controls = renderContact(showError, err);
        onSubmit = controls._submit;
        continueLabel = 'Send application';
        break;
      }
      default:
        controls = h('p', { text: 'Unknown question type.' });
        onSubmit = () => next();
    }

    const hasBack = state.history.length > 0;
    const nav = h('div', { class: 'qf-nav' },
      hasBack ? h('button', { type: 'button', class: 'linkbtn qf-back', onclick: back, text: 'Back' }) : h('span'),
      h('button', { type: 'submit', class: 'btn qf-continue', hidden: !showContinue, text: continueLabel }));

    form.append(label);
    if (help) form.append(help);
    if (controls) form.append(controls);
    form.append(err, nav);
    form.addEventListener('submit', (e) => { e.preventDefault(); onSubmit(); });

    swap(form, focus);
    if (lastViewed !== screen.id) {
      lastViewed = screen.id;
      track('step_view', { step_id: screen.id, step_index: stepIndex(screen) });
    }
    currentKeys = { screen, form };
  }

  function matchesWhen(when) {
    if (!when) return false;
    const v = state.answers[when.q];
    if (when.in) return when.in.includes(v);
    if ('eq' in when) return v === when.eq;
    return false;
  }

  function swap(node, focus) {
    stage.replaceChildren(node);
    if (!reduceMotion) {
      node.classList.add('qf-enter');
      requestAnimationFrame(() => requestAnimationFrame(() => node.classList.remove('qf-enter')));
    }
    if (focus) {
      const target = node.querySelector('#qf-label, h1');
      if (target) target.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  // ----------------------------------------------------------- measure
  function renderMeasure(showError, err) {
    const a = state.answers;
    const wrap = h('div', { class: 'qf-measure' });
    const toggle = h('div', { class: 'qf-units', role: 'group', 'aria-label': 'Units' });
    const metricBtn = h('button', { type: 'button', class: 'qf-unit', 'aria-pressed': String(state.units === 'metric'), text: 'cm and kg' });
    const imperialBtn = h('button', { type: 'button', class: 'qf-unit', 'aria-pressed': String(state.units === 'imperial'), text: 'ft, in and lb' });
    toggle.append(metricBtn, imperialBtn);
    const fields = h('div', { class: 'qf-mfields' });
    wrap.append(toggle, fields);

    const num = (id, labelText, unit, value) => {
      const input = h('input', { id, class: 'qf-input qf-input--num', type: 'text', inputmode: 'decimal', autocomplete: 'off', value: value != null ? String(value) : '', 'aria-describedby': 'qf-err' });
      return h('label', { class: 'qf-mfield', for: id }, h('span', { class: 'qf-mlabel', text: labelText }), h('span', { class: 'qf-mrow' }, input, h('span', { class: 'qf-unit-suffix', text: unit })));
    };
    const parse = (v) => { const n = Number(String(v).replace(',', '.').trim()); return Number.isFinite(n) && String(v).trim() !== '' ? n : null; };

    function draw() {
      fields.replaceChildren();
      if (state.units === 'metric') {
        fields.append(num('qf-h', 'Height', 'cm', a.height_cm != null ? Math.round(a.height_cm) : null), num('qf-w', 'Weight', 'kg', a.weight_kg != null ? Math.round(a.weight_kg * 10) / 10 : null));
      } else {
        const totalIn = a.height_cm != null ? a.height_cm / 2.54 : null;
        const ft = totalIn != null ? Math.floor(totalIn / 12) : null;
        const inch = totalIn != null ? Math.round(totalIn - ft * 12) : null;
        fields.append(
          h('div', { class: 'qf-mpair' }, num('qf-ft', 'Height, feet', 'ft', ft), num('qf-in', 'Inches', 'in', inch)),
          num('qf-lb', 'Weight', 'lb', a.weight_kg != null ? Math.round(a.weight_kg * 2.20462) : null));
      }
      fields.querySelectorAll('input').forEach((i) => i.addEventListener('input', () => { i.removeAttribute('aria-invalid'); err.hidden = true; }));
    }
    metricBtn.addEventListener('click', () => { state.units = 'metric'; save(); metricBtn.setAttribute('aria-pressed', 'true'); imperialBtn.setAttribute('aria-pressed', 'false'); draw(); });
    imperialBtn.addEventListener('click', () => { state.units = 'imperial'; save(); metricBtn.setAttribute('aria-pressed', 'false'); imperialBtn.setAttribute('aria-pressed', 'true'); draw(); });
    draw();

    wrap._submit = () => {
      let cm; let kg;
      if (state.units === 'metric') {
        cm = parse(fields.querySelector('#qf-h').value); kg = parse(fields.querySelector('#qf-w').value);
        if (cm === null || cm < 120 || cm > 230) return showError('Enter your height in centimetres, between 120 and 230. For example 178.', fields.querySelector('#qf-h'));
        if (kg === null || kg < 35 || kg > 300) return showError('Enter your weight in kilograms, between 35 and 300. For example 82.5.', fields.querySelector('#qf-w'));
      } else {
        const ft = parse(fields.querySelector('#qf-ft').value); const inch = parse(fields.querySelector('#qf-in').value) ?? 0; const lb = parse(fields.querySelector('#qf-lb').value);
        if (ft === null || ft < 4 || ft > 7 || inch < 0 || inch >= 12) return showError('Enter your height as feet and inches, for example 5 ft 10 in. Inches go from 0 to 11.', fields.querySelector('#qf-ft'));
        if (lb === null || lb < 77 || lb > 660) return showError('Enter your weight in pounds, between 77 and 660.', fields.querySelector('#qf-lb'));
        cm = (ft * 12 + inch) * 2.54; kg = lb / 2.20462;
      }
      state.answers.height_cm = Math.round(cm * 10) / 10;
      state.answers.weight_kg = Math.round(kg * 10) / 10;
      state.answers.body = `${state.answers.height_cm} cm, ${state.answers.weight_kg} kg`;
      save();
      next();
    };
    return wrap;
  }

  // ----------------------------------------------------------- contact
  function renderContact(showError, err) {
    const a = state.answers.contact || {};
    const wrap = h('div', { class: 'qf-contact' });
    const field = (id, labelText, attrs, hint) => {
      const input = h('input', { id, class: 'qf-input', 'aria-describedby': hint ? `${id}-hint qf-err` : 'qf-err', ...attrs });
      return h('div', { class: 'qf-cfield' },
        h('label', { for: id, class: 'qf-clabel', text: labelText }),
        hint ? h('p', { class: 'small muted', id: `${id}-hint`, text: hint }) : null,
        input);
    };
    const first = field('qf-first', 'First name', { type: 'text', autocomplete: 'given-name', value: a.first_name || '', maxlength: '60', required: true });
    const email = field('qf-email', 'Email', { type: 'email', inputmode: 'email', autocomplete: 'email', value: a.email || '', maxlength: '254', required: true });
    const handle = field('qf-handle', 'Phone number or Instagram', { type: 'text', autocomplete: 'tel', value: a.handle || '', maxlength: '80', required: true }, 'Where we can message you, for example +386 40 123 456 or @yourname.');
    // No call to schedule any more, so the time zone is taken from the browser, not asked.
    const guess = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return ''; } })();

    const consentBox = h('input', { type: 'checkbox', id: 'qf-consent', class: 'qf-check', checked: !!a.consent, 'aria-describedby': 'qf-err' });
    const consent = h('div', { class: 'qf-consent' }, consentBox,
      h('label', { for: 'qf-consent' }, schema.consent.text, ' ',
        h('a', { href: cfg.privacyUrl, target: '_blank', rel: 'noopener', text: 'Privacy Policy' }), '.'));

    // A field no person sees. Bots fill it; the server discards those.
    const trap = h('div', { class: 'qf-trap', 'aria-hidden': 'true' },
      h('label', { for: 'qf-website', text: 'Website' }), h('input', { id: 'qf-website', type: 'text', tabindex: '-1', autocomplete: 'off' }));

    wrap.append(first, email, handle, consent, trap);

    const persist = () => {
      state.answers.contact = {
        first_name: wrap.querySelector('#qf-first').value.trim(),
        email: wrap.querySelector('#qf-email').value.trim(),
        handle: wrap.querySelector('#qf-handle').value.trim(),
        timezone: a.timezone || guess,
        consent: consentBox.checked,
      };
      save();
    };
    wrap.addEventListener('input', () => { persist(); err.hidden = true; wrap.querySelectorAll('[aria-invalid]').forEach((x) => x.removeAttribute('aria-invalid')); });
    wrap.addEventListener('change', persist);

    wrap._submit = () => {
      persist();
      const c = state.answers.contact;
      if (!c.first_name) return showError('Add your first name so we know who we\'re talking to.', wrap.querySelector('#qf-first'));
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(c.email)) return showError('That email address doesn\'t look complete. Check for a missing @ or domain, like name@example.com.', wrap.querySelector('#qf-email'));
      if (c.handle.replace(/[^0-9A-Za-z]/g, '').length < 3) return showError('Add a phone number or your Instagram name, so we can reach you if we can help.', wrap.querySelector('#qf-handle'));
      if (!c.consent) return showError('Tick the consent box. We can\'t assess an application without storing it.', consentBox);
      submit(wrap.querySelector('#qf-website').value);
    };
    return wrap;
  }

  // ------------------------------------------------------------ outcomes
  function finishHardStop(stop) {
    track('qualifier_result', { outcome: stop.outcome, reason: stop.reason || '', stage: 'hard_stop' });
    // Nothing from a hard stop is sent anywhere, and nothing is kept.
    clear();
    state = { ...fresh(), done: { outcome: stop.outcome, reason: stop.reason } };
    renderOutcome(state.done, { focus: true });
  }

  function payloadFor(result, honeypot) {
    const answers = {};
    for (const s of flow()) {
      if (s.type === 'interstitial' || s.type === 'contact') continue;
      if (s.type === 'measure') { answers.height_cm = state.answers.height_cm; answers.weight_kg = state.answers.weight_kg; continue; }
      if (state.answers[s.id] !== undefined) answers[s.id] = state.answers[s.id];
    }
    const c = state.answers.contact || {};
    return {
      form: schema.id,
      schema_version: schema.version,
      submitted_at: new Date().toISOString(),
      duration_s: Math.round((Date.now() - (state.startedAt || Date.now())) / 1000),
      outcome: result.outcome,
      score: result.score,
      flags: result.flags,
      reason: result.reason,
      recommendation: result.recommendation || null,
      reasons: result.reasons || [],
      read_first: { why_failed: answers.why_failed || '', why_now: answers.why_now || '' },
      answers,
      contact: { first_name: c.first_name, email: c.email, handle: c.handle || '', timezone: c.timezone || '' },
      consent: { version: schema.consent.version, given_at: new Date().toISOString(), text: schema.consent.text },
      website: honeypot || '',
    };
  }

  async function post(body) {
    const res = await fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json().catch(() => ({}));
  }

  async function submit(honeypot) {
    const result = evaluate(state.answers, schema);
    const body = payloadFor(result, honeypot);
    renderSending();
    let data; let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try { data = await post(body); break; } catch (e) {
        lastError = e;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 1500));
      }
    }
    if (!data) return renderFailure(body, lastError);

    const outcome = data.outcome || result.outcome;
    track('qualifier_result', { outcome, reason: data.reason || result.reason || '', stage: 'submitted' });
    const email = body.contact.email;
    clear();
    state = { ...fresh(), done: { outcome, reason: data.reason || result.reason, email, id: data.id || null } };
    // Keep only the outcome, so a refresh on the result screen still shows it.
    try { sessionStorage.setItem(KEY + ':done', JSON.stringify(state.done)); } catch (e) {}
    renderOutcome(state.done, { focus: true });
  }

  function shell(title, ...kids) {
    root.dataset.state = 'result';
    progressEl.hidden = true;
    const node = h('div', { class: 'qf-screen qf-result' }, h('h1', { class: 'qf-q', id: 'qf-label', tabindex: '-1', text: title }), ...kids);
    return node;
  }

  function renderSending() {
    const node = shell('Sending your application…', h('p', { class: 'qf-help', text: 'This takes a second or two.' }));
    node.setAttribute('aria-busy', 'true');
    swap(node, true);
  }

  function renderFailure(body, error) {
    const what = error && /HTTP (\d+)/.test(error.message)
      ? `The server answered with an error (${error.message}).`
      : 'The server didn\'t answer. That can be your connection or ours.';
    const retry = h('button', { type: 'button', class: 'btn', text: 'Try again', onclick: () => submit(body.website) });
    const mail = h('a', { class: 'btn btn--quiet', href: mailto(body), text: 'Email it to us instead' });
    const node = shell('Your application didn\'t reach us.',
      h('p', { class: 'qf-help', text: `${what} The page tried twice. Your answers are still saved in this browser, so nothing is lost.` }),
      h('p', { class: 'qf-help', text: 'Try again in a moment, or send the same answers by email. The email button opens your mail app with everything filled in.' }),
      h('div', { class: 'qf-actions' }, retry, mail));
    swap(node, true);
    track('qualifier_result', { outcome: 'SUBMIT_FAILED', reason: String(error && error.message || 'network'), stage: 'submit' });
  }

  function mailto(body) {
    const lines = [];
    const labelOf = (s, v) => {
      if (Array.isArray(v)) return v.map((x) => labelOf(s, x)).join(', ');
      const o = (s.options || []).find((x) => x.value === v);
      return o ? fill(o.label) : String(v);
    };
    lines.push(`Name: ${body.contact.first_name}`, `Email: ${body.contact.email}`, `Phone or Instagram: ${body.contact.handle}`, '');
    lines.push(`Why it didn't work: ${body.read_first.why_failed}`, `Why now: ${body.read_first.why_now}`, '');
    for (const s of flow()) {
      if (['interstitial', 'contact'].includes(s.type) || ['six_months', 'why_failed', 'why_now'].includes(s.id)) continue;
      if (s.type === 'measure') { lines.push(`Height and weight: ${body.answers.height_cm} cm, ${body.answers.weight_kg} kg`); continue; }
      if (body.answers[s.id] === undefined) continue;
      lines.push(`${fill(s.label)} ${labelOf(s, body.answers[s.id])}`);
    }
    lines.push('', `Consent given (version ${body.consent.version}).`);
    let text = lines.join('\n');
    if (text.length > 1800) text = text.slice(0, 1800) + '\n[shortened]';
    return `mailto:${schema.support_email}?subject=${encodeURIComponent('Application')}&body=${encodeURIComponent(text)}`;
  }

  function resourceList(text, reason) {
    return String(text || '').split('\n').map((l) => l.split('|').map((x) => x.trim()))
      .filter((p) => !/TODO/i.test(p.join('|')))
      .filter((p) => (reason ? p.length >= 3 && p[0] === reason && /^https:\/\//.test(p[2]) : p.length >= 2 && /^https:\/\//.test(p[1])));
  }

  function renderOutcome(done, { focus }) {
    const o = schema.outcomes;
    let node;
    switch (done.outcome) {
      // No booking step: whoever fits, the coaches contact them.
      case 'ACCEPT':
        node = shell(fill(o.ACCEPT.title), h('p', { class: 'qf-help', text: fill(o.ACCEPT.body) }));
        break;
      case 'REVIEW':
        node = shell(fill(o.REVIEW.title), h('p', { class: 'qf-help', text: fill(o.REVIEW.body) }));
        break;
      case 'DECLINE': {
        const d = o.DECLINE[done.reason] || o.DECLINE.score;
        const res = resourceList(cfg.declineResources, done.reason || 'score');
        node = shell(fill(d.title),
          h('p', { class: 'qf-help', text: fill(d.why) }),
          h('div', { class: 'qf-first' }, h('h2', { class: 'h3', text: 'The one thing to do first' }), h('p', { text: fill(d.first) })),
          res.length ? h('ul', { class: 'qf-links', role: 'list' }, res.map((r) => h('li', {}, h('a', { href: r[2], rel: 'noopener', target: '_blank', text: r[1] })))) : null,
          h('p', { class: 'qf-help', text: fill(o.DECLINE.reapply) }),
          done.email ? null : h('p', { class: 'small muted', text: 'Nothing you entered has been sent or saved.' }));
        break;
      }
      case 'AGE_EXIT':
        node = o.AGE_EXIT ? shell(fill(o.AGE_EXIT.title), h('p', { class: 'qf-help', text: fill(o.AGE_EXIT.body) })) : shell('Thank you.');
        break;
      case 'CARE_EXIT': {
        const res = resourceList(cfg.careResources);
        node = shell(fill(o.CARE_EXIT.title),
          h('p', { class: 'qf-help', text: fill(o.CARE_EXIT.body) }),
          res.length
            ? h('ul', { class: 'qf-links', role: 'list' }, res.map((r) => h('li', {}, h('a', { href: r[1], rel: 'noopener', target: '_blank', text: r[0] }))))
            : h('p', { class: 'qf-help', text: o.CARE_EXIT.fallback }),
          h('p', { class: 'small muted', text: o.CARE_EXIT.note }));
        break;
      }
      default:
        node = shell('Thank you.');
    }
    swap(node, focus);
  }

  // ------------------------------------------------------------ keyboard
  let currentKeys = null;
  document.addEventListener('keydown', (e) => {
    if (!currentKeys || state.done) return;
    const { screen, form } = currentKeys;
    const t = e.target;
    const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT');
    if (e.key === 'Escape') { if (typing) t.blur(); return; } // never clears anything
    if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^[1-9]$/.test(e.key) && (screen.type === 'single' || screen.type === 'multi')) {
      const btn = form.querySelectorAll('.qf-opt')[Number(e.key) - 1];
      if (btn) { e.preventDefault(); btn.click(); }
    } else if (e.key === 'Enter' && (t === document.body || (t && t.id === 'qf-label'))) {
      e.preventDefault(); form.requestSubmit();
    }
  });

  // --------------------------------------------------------------- start
  try {
    const done = JSON.parse(sessionStorage.getItem(KEY + ':done') || 'null');
    if (done && done.outcome) { state = { ...fresh(), done }; }
  } catch (e) {}
  root.querySelector('[data-qf-loading]')?.remove();
  render({ focus: false });
}
